import { z } from "zod";
export const pluginGraduationTargetSchema = z.enum([
    "native-ingestion",
    "native-runtime",
    "composio-bridge-retirement",
]);
export const pluginDemandObservationsSchema = z.object({
    invocations: z.number().int().nonnegative(),
    succeeded: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    activeUsers: z.number().int().nonnegative(),
    activeOrganisations: z.number().int().nonnegative(),
    activeDays: z.number().int().nonnegative(),
    successRateBasisPoints: z.number().int().min(0).max(10_000),
    lastInvokedAt: z.number().nonnegative().nullable(),
}).strict().superRefine((observed, context) => {
    if (observed.invocations !== observed.succeeded + observed.failed) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["invocations"],
            message: "Demand invocations must equal succeeded plus failed.",
        });
    }
    if (observed.invocations === 0 && observed.lastInvokedAt !== null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["lastInvokedAt"],
            message: "Zero demand cannot have a last invocation.",
        });
    }
    if (observed.invocations > 0 && observed.lastInvokedAt === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["lastInvokedAt"],
            message: "Measured demand must include its last invocation.",
        });
    }
    const expectedSuccessRate = observed.invocations === 0
        ? 0
        : Math.floor(observed.succeeded * 10_000 / observed.invocations);
    if (observed.successRateBasisPoints !== expectedSuccessRate) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["successRateBasisPoints"],
            message: "Demand success rate must match the invocation totals.",
        });
    }
});
export const pluginDemandThresholdsSchema = z.object({
    minimumInvocations: z.number().int().positive(),
    minimumActiveUsers: z.number().int().positive(),
    minimumActiveOrganisations: z.number().int().positive(),
    minimumActiveDays: z.number().int().positive(),
    minimumSuccessRateBasisPoints: z.number().int().min(1).max(10_000),
}).strict();
export const pluginDemandChecksSchema = z.object({
    invocations: z.boolean(),
    activeUsers: z.boolean(),
    activeOrganisations: z.boolean(),
    activeDays: z.boolean(),
    successRate: z.boolean(),
}).strict();
export const pluginDemandGateResponseSchema = z.object({
    candidateId: z.string().trim().min(1),
    pluginSlug: z.string().trim().min(1),
    target: pluginGraduationTargetSchema,
    plane: z.literal("runtime"),
    source: z.literal("task_run_events"),
    ingestionIncluded: z.literal(false),
    window: z.object({
        hours: z.number().int().positive(),
        startedAt: z.number().nonnegative(),
        endedAt: z.number().nonnegative(),
    }).strict(),
    thresholds: pluginDemandThresholdsSchema,
    observed: pluginDemandObservationsSchema,
    checks: pluginDemandChecksSchema,
    demandThresholdPassed: z.boolean(),
    operatorApprovalRequired: z.literal(true),
    automaticProvisioning: z.literal(false),
    disposition: z.enum(["observe", "operator-review"]),
}).strict().superRefine((gate, context) => {
    const allChecksPassed = Object.values(gate.checks).every(Boolean);
    if (gate.demandThresholdPassed !== allChecksPassed) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["demandThresholdPassed"],
            message: "Demand threshold status must match every check.",
        });
    }
    if ((gate.disposition === "operator-review") !== allChecksPassed) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["disposition"],
            message: "Only a passed demand threshold can enter operator review.",
        });
    }
});
export const composioBridgeRetirementCandidateSchema = z.object({
    candidateId: z.string().trim().min(1),
    pluginSlug: z.string().trim().min(1),
    externalProvider: z.literal("composio"),
    externalSlug: z.string().trim().min(1),
    dynamicPluginSlug: z.string().trim().min(1).nullable(),
    capabilityParity: z.enum(["complete", "incomplete"]),
    parityEvidence: z.string().trim().min(1),
    claimedAliasInvocations30d: z.number().int().nonnegative(),
    activeLegacySessions: z.number().int().nonnegative(),
    defaultRoutingArchitecture: z.string().trim().min(1),
    defaultUsesCanonicalCatalog: z.boolean(),
    evidenceSha256: z.string().regex(/^[a-f0-9]{64}$/u),
    eligibleForOperatorApproval: z.boolean(),
    approved: z.boolean(),
    approvedAt: z.number().nonnegative().nullable(),
    disposition: z.enum([
        "parity-blocked",
        "telemetry-blocked",
        "router-blocked",
        "operator-review",
        "retired",
    ]),
}).strict().superRefine((candidate, context) => {
    const eligible = candidate.capabilityParity === "complete" &&
        candidate.claimedAliasInvocations30d === 0 &&
        candidate.activeLegacySessions === 0 &&
        candidate.defaultUsesCanonicalCatalog;
    if (candidate.eligibleForOperatorApproval !== eligible) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["eligibleForOperatorApproval"],
            message: "Bridge retirement eligibility must match its evidence.",
        });
    }
    if (candidate.approved !== (candidate.disposition === "retired")) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["disposition"],
            message: "Only an approved bridge can be retired.",
        });
    }
});
export const listComposioBridgeRetirementCandidatesResponseSchema = z.object({
    target: z.literal("composio-bridge-retirement"),
    telemetryWindowHours: z.literal(720),
    ingestionIncluded: z.literal(false),
    candidates: z.array(composioBridgeRetirementCandidateSchema).max(100),
}).strict();
export const approveComposioBridgeRetirementRequestSchema = z.object({
    candidateId: z.string().trim().min(1).max(240),
    evidenceSha256: z.string().regex(/^[a-f0-9]{64}$/u),
}).strict();
export const approveComposioBridgeRetirementResponseSchema = z.object({
    approved: z.literal(true),
    candidate: composioBridgeRetirementCandidateSchema,
}).strict();
export const nativeProviderGraduationCandidateSchema = z.object({
    candidateId: z.string().trim().min(1),
    externalProvider: z.literal("composio"),
    externalSlug: z.string().trim().min(1),
    dynamicPluginSlug: z.string().trim().min(1),
    label: z.string().trim().min(1),
    toolCount: z.number().int().nonnegative(),
    authSchemes: z.array(z.string().trim().min(1)).max(20),
    managedAuthSchemes: z.array(z.string().trim().min(1)).max(20),
    nativeCandidatePluginSlug: z.string().trim().min(1).nullable(),
    nativeCandidateEnabled: z.boolean(),
    capabilityParity: z.enum(["complete", "unproven"]),
    parityEvidence: z.string().trim().min(1).nullable(),
    riskReview: z.enum(["approved", "missing"]),
    riskTier: z.enum(["low", "moderate", "high", "critical"]).nullable(),
    riskEvidence: z.string().trim().min(1).nullable(),
    thresholds: pluginDemandThresholdsSchema,
    observed: pluginDemandObservationsSchema,
    checks: pluginDemandChecksSchema,
    demandThresholdPassed: z.boolean(),
    reviewedEvidenceComplete: z.boolean(),
    evidenceSha256: z.string().regex(/^[a-f0-9]{64}$/u),
    eligibleForOperatorApproval: z.boolean(),
    operatorApprovalRequired: z.literal(true),
    automaticGraduation: z.literal(false),
    reviewedReleaseRequired: z.literal(true),
    approved: z.boolean(),
    approvedAt: z.number().nonnegative().nullable(),
    disposition: z.enum([
        "observe",
        "parity-blocked",
        "risk-blocked",
        "configuration-blocked",
        "operator-review",
        "approved-awaiting-release",
    ]),
}).strict().superRefine((candidate, context) => {
    const allDemandChecks = Object.values(candidate.checks).every(Boolean);
    if (candidate.demandThresholdPassed !== allDemandChecks) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["demandThresholdPassed"],
            message: "Native graduation demand status must match every check.",
        });
    }
    const reviewedEvidenceComplete = candidate.nativeCandidatePluginSlug !== null &&
        !candidate.nativeCandidateEnabled &&
        candidate.capabilityParity === "complete" &&
        candidate.parityEvidence !== null &&
        candidate.riskReview === "approved" &&
        candidate.riskTier !== null && candidate.riskEvidence !== null;
    if (candidate.reviewedEvidenceComplete !== reviewedEvidenceComplete) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["reviewedEvidenceComplete"],
            message: "Reviewed native graduation evidence is inconsistent.",
        });
    }
    if (candidate.eligibleForOperatorApproval !==
        (allDemandChecks && reviewedEvidenceComplete)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["eligibleForOperatorApproval"],
            message: "Native graduation eligibility must match demand and review evidence.",
        });
    }
    if (candidate.approved !==
        (candidate.disposition === "approved-awaiting-release")) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["disposition"],
            message: "An approval receipt must await a separately reviewed release.",
        });
    }
});
export const listNativeProviderGraduationCandidatesResponseSchema = z.object({
    target: z.literal("native-runtime"),
    plane: z.literal("runtime"),
    source: z.literal("task_run_events"),
    ingestionIncluded: z.literal(false),
    window: z.object({
        hours: z.literal(720),
        startedAt: z.number().nonnegative(),
        endedAt: z.number().nonnegative(),
    }).strict(),
    totalCandidates: z.number().int().nonnegative(),
    returnedCandidates: z.number().int().nonnegative(),
    truncated: z.boolean(),
    candidates: z.array(nativeProviderGraduationCandidateSchema).max(200),
}).strict();
export const approveNativeProviderGraduationRequestSchema = z.object({
    candidateId: z.string().trim().min(1).max(240),
    evidenceSha256: z.string().regex(/^[a-f0-9]{64}$/u),
}).strict();
export const approveNativeProviderGraduationResponseSchema = z.object({
    approved: z.literal(true),
    candidate: nativeProviderGraduationCandidateSchema,
}).strict();
